import { useRef, useState } from 'react'
import StarIcons from '@/icons/StarIcons'
import { cn } from '@/lib/utils'

interface StarRatingProps {
    value: number
    onChange?: (value: number) => void
    readOnly?: boolean
    size?: 'sm' | 'md' | 'lg'
    className?: string
    /**
     * Hiển thị điểm lẻ (4.6 -> sao thứ 5 tô 60%). Chỉ tác dụng khi readOnly — người
     * dùng vẫn chỉ chọn được số nguyên. Dùng cho điểm trung bình.
     */
    precise?: boolean
    /** Nhãn cho screen reader; component không tự i18n nên caller truyền vào. */
    ariaLabel?: string
}

const SIZE: Record<NonNullable<StarRatingProps['size']>, string> = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-9 h-9',
}

const STARS = [1, 2, 3, 4, 5]

// StarRating dùng chung, rút từ renderStars của PublicFeedback (state hover/selected
// đã được nâng lên props value/onChange). @/icons/StarIcons tô màu qua currentColor.
export const StarRating = ({
    value,
    onChange,
    readOnly = false,
    size = 'md',
    className,
    precise = false,
    ariaLabel,
}: StarRatingProps) => {
    const [hovered, setHovered] = useState(0)
    const btnRefs = useRef<(HTMLButtonElement | null)[]>([])
    const interactive = !readOnly && !!onChange
    const active = interactive ? hovered || value : value

    const star = (filled: boolean) => (
        <StarIcons
            className={cn(
                SIZE[size],
                filled ? 'text-yellow-400' : 'text-gray-300',
                'transition-colors'
            )}
        />
    )

    // Điểm lẻ: mỗi sao là 2 lớp xếp chồng, lớp vàng clip theo phần được tô của CHÍNH
    // sao đó — nên chính xác bất kể gap giữa các sao.
    if (readOnly && precise) {
        return (
            <div
                className={cn('flex items-center gap-1', className)}
                role="img"
                aria-label={ariaLabel ?? `${value}/5`}
            >
                {STARS.map((s) => (
                    <span
                        key={s}
                        className="relative inline-flex"
                        aria-hidden="true"
                    >
                        {star(false)}
                        <span
                            className="absolute inset-y-0 left-0 overflow-hidden"
                            style={{
                                width: `${
                                    Math.max(0, Math.min(1, value - (s - 1))) *
                                    100
                                }%`,
                            }}
                        >
                            {star(true)}
                        </span>
                    </span>
                ))}
            </div>
        )
    }

    // Chỉ đọc: role="img" + aria-label để screen reader đọc được điểm (trước đây là
    // <span> trần, không có text nào).
    if (!interactive) {
        return (
            <div
                className={cn('flex items-center gap-1', className)}
                role="img"
                aria-label={ariaLabel ?? `${value}/5`}
            >
                {STARS.map((s) => (
                    <span key={s} aria-hidden="true">
                        {star(s <= active)}
                    </span>
                ))}
            </div>
        )
    }

    // Bàn phím: mũi tên tăng/giảm, Home/End về 1/5.
    //
    // select() PHẢI kéo DOM focus theo, không chỉ đổi value. Nếu không: tabIndex dời
    // sang sao mới nhưng focus vẫn ở sao cũ, và Enter/Space kích hoạt button ĐANG
    // FOCUS -> người dùng nhấn End (chọn 5) rồi Enter và gửi đi 1 sao. Đây cũng là
    // yêu cầu của ARIA radiogroup: mũi tên phải di chuyển focus.
    const select = (next: number) => {
        const clamped = Math.min(5, Math.max(1, next))
        onChange?.(clamped)
        btnRefs.current[clamped - 1]?.focus()
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const step: Record<string, number> = {
            ArrowRight: 1,
            ArrowUp: 1,
            ArrowLeft: -1,
            ArrowDown: -1,
        }
        // Object.hasOwn, không phải `in`: `in` đi cả prototype chain nên "constructor"
        // hay "toString" sẽ khớp và cho ra step undefined -> NaN.
        if (Object.hasOwn(step, e.key)) {
            e.preventDefault()
            select((value || 0) + step[e.key])
            return
        }
        if (e.key === 'Home') {
            e.preventDefault()
            select(1)
        }
        if (e.key === 'End') {
            e.preventDefault()
            select(5)
        }
    }

    const selected = Math.round(value)
    // Roving tabIndex: radiogroup chỉ chiếm 1 tab-stop như radio group thật.
    const focusable = selected >= 1 && selected <= 5 ? selected : 1

    return (
        <div
            className={cn('flex items-center gap-1', className)}
            role="radiogroup"
            aria-label={ariaLabel}
            onKeyDown={handleKeyDown}
        >
            {STARS.map((s) => (
                <button
                    key={s}
                    ref={(el) => {
                        btnRefs.current[s - 1] = el
                    }}
                    type="button"
                    role="radio"
                    aria-checked={s === selected}
                    aria-label={`${s}/5`}
                    tabIndex={s === focusable ? 0 : -1}
                    className="rounded transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
                    onMouseEnter={() => setHovered(s)}
                    onMouseLeave={() => setHovered(0)}
                    onClick={() => onChange?.(s)}
                >
                    {star(s <= active)}
                </button>
            ))}
        </div>
    )
}

export default StarRating
